import { useMemo } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, MessageSquare, ChevronRight, Search, Filter, TrendingUp } from 'lucide-react';
import { LeadRecord, Priority } from '../data/mockData';
import { cn } from '../../lib/utils';
import { CreditRatingBadge, LoginStatusBadge, DocumentStatusBadge, ProductsCell, PriorityDropdown, StatusDropdown, CustomerStatusOption } from './TableCells';
import { format } from 'date-fns';
import { Stage } from './TopBar';
import { useNavigate } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { SortField, SortOrder } from './sort';

interface AdminUserList {
  id: number;
  name: string;
}

interface DataTableProps {
  data: LeadRecord[];
  activeStage: Stage;
  statusOptions: CustomerStatusOption[];
  onRowClick: (record: LeadRecord) => void;
  onInteractionClick: (record: LeadRecord) => void;
  onUpdateRecord: (id: string, updates: Partial<LeadRecord>) => void;
  adminUserList?: AdminUserList[];
  sortField?: SortField;
  sortOrder?: SortOrder;
  onSortChange: (field: SortField) => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function formatDate(date: Date): string {
  return format(date, 'MMM dd, yyyy');
}

function truncateText(text: string, maxLength: number): string {
  return text.length <= maxLength ? text : `${text.slice(0, maxLength)}...`;
}

function getInitials(text: string): string {
  if (!text || typeof text !== 'string') return '';
  const parts = text.trim().split(/\s+/).filter(Boolean);
  const letters =
    parts.length >= 2
      ? `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`
      : (parts[0]?.slice(0, 2) ?? '');
  return letters.toUpperCase();
}

function getTextValue(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  return '';
}

function getTotalSanctioned(record: LeadRecord): number {
  return record.sanctionAmount || 0;
}

function getUtilise(record: LeadRecord): number {
  return record.disbursements || 0;
}

function getAvailable(record: LeadRecord): number {
  return getTotalSanctioned(record) - getUtilise(record);
}

export function DataTable({
  data,
  activeStage,
  statusOptions,
  onRowClick,
  onInteractionClick,
  onUpdateRecord,
  adminUserList,
  sortField,
  sortOrder,
  onSortChange,
}: DataTableProps) {
  const navigate = useNavigate();

  const isPipeline = activeStage === 'pipeline';
  const isActivePortfolio = activeStage === 'active-portfolio';
  const sortedData = data;

  const totalDealValue = useMemo(() => data.reduce((sum, r) => sum + r.dealSize, 0), [data]);

  const SortableHeader = ({
    field,
    label,
    className,
    align,
  }: {
    field: SortField;
    label: string;
    className?: string;
    align?: 'right';
  }) => (
        <th className={cn('data-table-cell bg-muted text-left text-[11px] font-medium tracking-wide text-muted-foreground', align === 'right' && 'text-right', className)}>
      <button
        onClick={() => onSortChange(field)}
        className={cn('group/sort flex items-center gap-1 transition-colors hover:text-foreground', align === 'right' && 'w-full justify-end')}
      >
        {label}
        {sortField === field ? (
          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-accent-foreground" /> : <ArrowDown className="h-3 w-3 text-accent-foreground" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground/30 opacity-0 transition-opacity group-hover/sort:opacity-100" />
        )}
      </button>
    </th>
  );

  const getColSpan = () => {
    if (isActivePortfolio) return 13;
    if (isPipeline) return 15;
    return 16;
  };

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-[24px] border border-slate-200 bg-white py-16 shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
        <div className="relative mb-6">
          <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-muted">
            <Search className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <div className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-lg bg-accent/10">
            <Filter className="h-3.5 w-3.5 text-accent-foreground" />
          </div>
        </div>
        <h3 className="mb-1 text-base font-semibold text-foreground">No matching records</h3>
        <p className="mb-4 max-w-sm text-center text-sm text-muted-foreground">
          Try adjusting your search criteria or filters.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
      <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
        <div className={cn('min-w-[1400px]', !isActivePortfolio && 'min-w-[1800px]')}>
          <table className="w-full">
            <thead className="sticky top-0 z-30 data-table-header">
              <tr className="border-b">
                <th className="frozen-column data-table-cell z-40 min-w-[140px] max-w-[180px] bg-white text-left text-[11px] font-medium tracking-wide text-muted-foreground shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                  <button onClick={() => onSortChange('companyName')} className="flex items-center gap-1 bg-white transition-colors hover:text-foreground">
                    Company
                    {sortField === 'companyName' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-accent-foreground" /> : <ArrowDown className="h-3 w-3 text-accent-foreground" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-muted-foreground/30" />
                    )}
                  </button>
                </th>
                <SortableHeader field="leadRM" label="AF Lead" className="min-w-[120px]" />
                <SortableHeader field="partnerCompanyName" label="Partner Owner" className="min-w-[130px]" />
                <SortableHeader field="rating" label="Rating" className="min-w-[80px]" />
                <SortableHeader field="city" label="City" className="min-w-[100px]" />
                <th className="data-table-cell min-w-[150px] bg-muted text-left text-[11px] font-medium tracking-wide text-muted-foreground">Product</th>
                <th className="data-table-cell min-w-[110px] bg-muted text-right text-[11px] font-medium tracking-wide text-muted-foreground">
                  <button onClick={() => onSortChange('dealSize')} className="flex w-full items-center justify-end gap-1 transition-colors hover:text-foreground">
                    Deal Value
                    {sortField === 'dealSize' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-accent-foreground" /> : <ArrowDown className="h-3 w-3 text-accent-foreground" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-muted-foreground/30" />
                    )}
                  </button>
                </th>

                {isPipeline && (
                  <>
                    <SortableHeader field="mraStatus" label="MRA" className="min-w-[80px]" />
                    <SortableHeader field="termSheetStatus" label="TS Signed" className="min-w-[90px]" />
                    <SortableHeader field="priority" label="Priority" className="min-w-[100px]" />
                    <SortableHeader field="pipeline" label="Status" className="min-w-[130px]" />
                  </>
                )}

                {isActivePortfolio && (
                  <>
                    <SortableHeader field="totalSanctioned" label="Total Sanctioned" className="min-w-[130px]" align="right" />
                    <SortableHeader field="utilise" label="Utilise" className="min-w-[100px]" align="right" />
                    <SortableHeader field="available" label="Available" className="min-w-[100px]" align="right" />
                  </>
                )}

                {!isPipeline && !isActivePortfolio && (
                  <>
                    <SortableHeader field="mraStatus" label="MRA" className="min-w-[80px]" />
                    <SortableHeader field="termSheetStatus" label="TS Signed" className="min-w-[90px]" />
                    <SortableHeader field="priority" label="Priority" className="min-w-[100px]" />
                    <SortableHeader field="pipeline" label="Status" className="min-w-[130px]" />
                    <SortableHeader field="loginStatus" label="Login" className="min-w-[90px]" />
                  </>
                )}

                <SortableHeader field="createdDate" label="Created Date" className="min-w-[110px]" />
                {!isActivePortfolio && <SortableHeader field="next_follow_up_date" label="Next Followup" className="min-w-[110px]" />}
                <th className="data-table-cell text-left font-medium text-muted-foreground text-[11px] tracking-wide bg-muted min-w-[180px]">
                  Last Interaction
                </th>
                <th className="data-table-cell w-10 bg-muted" />
              </tr>
            </thead>
            <tbody>
              {sortedData.map((record, index) => (
                <tr
                  key={record.id}
                  className={cn(
                    'data-row cursor-pointer border-b last:border-0 transition-colors duration-150',
                    index % 2 === 0 ? 'bg-card' : 'bg-muted/20'
                  )}
                  onClick={() => onRowClick(record)}
                >
                  <td className="frozen-column frozen-cell data-table-cell z-20 bg-white shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                    <div className="flex min-w-0 items-center gap-3 bg-white">
                      {record.companyLogo ? (
                        <img
                          src={record.companyLogo}
                          alt={record.companyName}
                          className="w-7 h-7 rounded-md flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-semibold text-muted-foreground">
                            {getInitials(record.companyName)}
                          </span>
                        </div>
                      )}
                      {/* <button
                        type="button"
                        className="block truncate text-left text-sm font-medium text-foreground transition-colors hover:text-accent-foreground leading-tight py-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/manage-customer/${record.id}`);
                        }}
                      >
                        {record.companyName}
                      </button> */}


                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="font-medium text-foreground text-sm block hover:text-accent-foreground transition-colors text-left leading-tight py-1 truncate max-w-[240px]"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/manage-customer/${record.id}`);
                              }}
                            >
                              {record.companyName}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>{record.companyName}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </td>

                  <td className="data-table-cell text-muted-foreground text-sm truncate max-w-[140px]">
                    {adminUserList?.find(a => String(a.id) === String(record.leadRM))?.name || "-"}
                  </td>
                  <td className="data-table-cell text-muted-foreground text-sm truncate max-w-[140px]">{getTextValue(record.partnerCompanyName) || '—'}</td>
                  <td className="data-table-cell"><CreditRatingBadge rating={record.rating} /></td>
                  <td className="data-table-cell text-muted-foreground text-sm">{getTextValue(record.city) || '—'}</td>
                  <td className="data-table-cell"><ProductsCell products={record.productTypeList} /></td>
                  <td className="data-table-cell text-right text-sm font-medium text-foreground">{formatCurrency(record.dealSize)}</td>

                  {isPipeline && (
                    <>
                      <td className="data-table-cell"><DocumentStatusBadge status={record.mraStatus} /></td>
                      <td className="data-table-cell"><DocumentStatusBadge status={record.termSheetStatus} /></td>
                      <td className="data-table-cell">
                        <PriorityDropdown value={(record.pipeline || '-') as Priority} onChange={(v) => onUpdateRecord(record.id, { pipeline: v })} />
                      </td>
                      <td className="data-table-cell">
                        <StatusDropdown
                          value={record.status}
                          statusId={record.statusId}
                          statusOptions={statusOptions}
                          onChange={(status) =>
                            onUpdateRecord(record.id, {
                              status: status.name,
                              statusId: String(status.id),
                            })
                          }
                        />
                      </td>
                    </>
                  )}

                  {isActivePortfolio && (
                    <>
                      <td className="data-table-cell text-right text-sm font-medium text-foreground">{formatCurrency(getTotalSanctioned(record))}</td>
                      <td className="data-table-cell text-right text-sm font-medium text-foreground">{formatCurrency(record.disbursements || 0)}</td>
                      <td className="data-table-cell text-right text-sm font-medium text-success">{formatCurrency(getAvailable(record))}</td>
                    </>
                  )}

                  {!isPipeline && !isActivePortfolio && (
                    <>
                      <td className="data-table-cell"><DocumentStatusBadge status={record.mraStatus} /></td>
                      <td className="data-table-cell"><DocumentStatusBadge status={record.termSheetStatus} /></td>
                      <td className="data-table-cell">
                        <PriorityDropdown value={(record.pipeline || '-') as Priority} onChange={(v) => onUpdateRecord(record.id, { pipeline: v })} />
                      </td>
                      <td className="data-table-cell">
                        <StatusDropdown
                          value={record.status}
                          statusId={record.statusId}
                          statusOptions={statusOptions}
                          onChange={(status) =>
                            onUpdateRecord(record.id, {
                              status: status.name,
                              statusId: String(status.id),
                            })
                          }
                        />
                      </td>
                      <td className="data-table-cell"><LoginStatusBadge status={record.loginStatus} /></td>
                    </>
                  )}

                  <td className="data-table-cell text-muted-foreground text-sm">{getTextValue(record.createdDate) || '—'}</td>
                  {!isActivePortfolio && (
                    <td className="data-table-cell text-sm text-muted-foreground">
                      {record.next_follow_up_date ? formatDate(record.next_follow_up_date) : <span className="text-muted-foreground/50">—</span>}
                    </td>
                  )}
                  <td className="data-table-cell">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onInteractionClick(record);
                      }}
                      className="flex items-center gap-2 text-left text-muted-foreground transition-colors hover:text-accent-foreground"
                    >
                      <MessageSquare className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="text-sm">
                        {record.comments?.[0]
                          ? truncateText(record.comments[record.comments.length - 1]?.text || '', 25)
                          : 'No interactions'}
                      </span>
                    </button>
                  </td>
                  <td className="data-table-cell">
                    <ChevronRight className="arrow-icon h-4 w-4 text-muted-foreground/40" />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t bg-muted/30">
              <tr>
                <td className="frozen-column frozen-column-footer data-table-cell z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Total</span>
                    <span className="rounded bg-accent/10 px-1.5 py-0.5 text-[11px] font-medium text-accent-foreground">{data.length} records</span>
                  </div>
                </td>
                <td className="data-table-cell" colSpan={5} />
                <td className="data-table-cell text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <TrendingUp className="h-3.5 w-3.5 text-success" />
                    <span className="text-sm font-semibold text-foreground">{formatCurrency(totalDealValue)}</span>
                  </div>
                </td>
                <td className="data-table-cell" colSpan={getColSpan() - 8} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
